import { useSyncExternalStore } from 'react';

import { SURFACES } from './color';

/**
 * Light and dark mode (D41). Each person picks Light, Dark or System in the account menu; the choice
 * is kept per device in this browser. Light and Dark set `data-theme` on <html>; System removes it and
 * the CSS media query follows the system. `public/theme-init.js` applies a saved choice before the page
 * paints, so a dark choice never flashes light.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

/** Also read by public/theme-init.js — keep them in step. */
export const THEME_STORAGE_KEY = 'tasklist-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readStored(): ThemePreference {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Kept in memory too, so the choice holds for this page where storage is blocked. */
let current = readStored();
const listeners = new Set<() => void>();

function apply(preference: ThemePreference) {
  if (preference === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', preference);
}

function systemIsDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(preference: ThemePreference): Theme {
  if (preference !== 'system') return preference;
  return systemIsDark() ? 'dark' : 'light';
}

/** Saves the choice and applies it at once. */
export function setThemePreference(preference: ThemePreference): void {
  current = preference;
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage blocked (private window): the choice holds until the page reloads.
  }
  apply(preference);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : undefined;
  media?.addEventListener('change', listener);
  // The choice changed in another tab.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    current = readStored();
    apply(current);
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    media?.removeEventListener('change', listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** One string, so React compares snapshots by value. */
function snapshot(): `${ThemePreference}:${Theme}` {
  return `${current}:${resolveTheme(current)}`;
}

export function useTheme() {
  const [preference, resolved] = useSyncExternalStore(subscribe, snapshot).split(':') as [
    ThemePreference,
    Theme,
  ];
  return { preference, resolved, setPreference: setThemePreference };
}

/** The card and page surfaces of the theme on screen, for colors computed at runtime. */
export function useSurfaces(): (typeof SURFACES)[Theme] {
  return SURFACES[useTheme().resolved];
}
